import moment from 'moment';

import store from '@/store';
import { specificColsProperties } from '../callLogsDefaultColumnState';
import { CALLLOG_REPORT_TYPE, INSIGHT_REPORT_TYPE } from '../store/state';
import { costCurrencyFormatter } from '../components/AgGridUtils';

export const HEADER_NAME_COLUMNS = ' Available Columns';
const EXPIRE_INSIGHTS_DATA_CACHE_AFTER_SECONDS = 60 * 2; // 2 mins

// Matches the lifetime the backend uses for recording links (PublicRecordingController)
export const RECORDING_TEMP_URL_LIFETIME_SECONDS = 6 * 60 * 60;
// Reuse minted URLs only briefly (replay/scrub bursts): a served URL must stay valid
// for an entire playback, and calls are known to run ~2 hours, so cap the cache well
// below lifetime − playback (a cache-served URL always has ≥5h of its 6h left)
const RECORDING_TEMP_URL_CACHE_MS = 60 * 60 * 1000;

export const colToAtributeIdMapping = {
  campaignName: 'campaignId',
  targetName: 'targetId',
  buyerTargetName: 'buyerTargetId',
  publisherName: 'publisherId',
  buyer: 'buyerId',
};


export const stateMapping = {
  CA: {
    routeIdAttribute: 'campaignId',
    routeName: 'dashboard.campaign'
  },
  TA: {
    routeIdAttribute: 'targetId',
    routeName: 'dashboard.target.edit'
  },
  PI: {
    routeIdAttribute: 'pingTreeTargetId',
    routeName: 'dashboard.ringtreetarget.edit'
  },
  AF: {
    routeIdAttribute: 'affiliateId',
    routeName: 'dashboard.affiliate.edit'
  },
  BU: {
    routeIdAttribute: 'buyerId',
    routeName: 'dashboard.buyer.edit'
  },
};

function compareCurrencyValues(valueA, valueB) {
  return valueA - valueB;
}

const statusToTooltipMapping = {
  'live': {
    tooltip: 'LIVE',
    cssClass: 'mark-call__status--live'
  },
  'completed': {
    tooltip: 'COMPLETED_NO_CONVERSION',
    cssClass: 'mark-call__status--completed'
  },
  'publisher-completed': {
    tooltip: 'COMPLETED',
    cssClass: 'mark-call__status--converted'
  },
  'converted': {
    tooltip: 'CONVERTED',
    cssClass: 'mark-call__status--converted'
  },
  'buyer-converted': {
    tooltip: 'PAID_OUT',
    cssClass: 'mark-call__status--converted'
  },
  'blocked': {
    tooltip: 'BLOCKED',
    cssClass: 'mark-call__status--blocked'
  },
  'notConnected': {
    tooltip: 'NOT_CONNECTED',
    cssClass: 'mark-call__status--blocked'
  }
};

const customColumnProps = {
  isDuplicate: { headerName: 'Duplicate', headerTooltip: 'Duplicate' },
  endCallSource: { headerName: 'Hangup', headerTooltip: 'Hangup' },
  earningsPerCallNet: { headerName: 'RPC Net', headerTooltip: 'RPC Net' },
  number: { headerName: 'Dialed #', headerTooltip: 'Dialed #' },
  blacklistCheckCost: { headerName: 'TCPA Shield Cost', headerTooltip: 'TCPA Shield Cost' },
  callCount: { hide: false, headerTooltip: 'All calls that have entered the Ringba system' },
  nonConnectedCallCount: { hide: false, headerTooltip: 'Calls that were initiated but did not connect to a target' },
  connectedCallCount: { hide: false, headerTooltip: 'Calls that successfully connected to a Target' },
  duplicateCalls: { hide: false, headerTooltip: 'The amount of calls made by a repeat caller.' },
  liveCallCount: { headerTooltip: 'Calls that are currently in progress' },
  completedCalls: { hide: false, headerTooltip: 'Calls that haven’t been blocked and have connected to a target or ringed' },
  endedCalls: { hide: false, headerTooltip: 'Calls that have entered and left the Ringba system. Does not include live calls.' },
  incompleteCalls: { headerName: 'IVR Hangup', headerTooltip: 'Calls that the user hung up during the IVR', hide: false, },
  convertedCalls: { hide: false, headerTooltip: 'Calls that have met the campaign’s conversion requirements' },
  blockedCalls: { hide: false, headerTooltip: 'Any call that was blocked before entering the Ringba system' },
  avgHandleTime: {
    hide: false,
    minWidth: 60,
    flex: 68,
    headerName: 'ACL',
    headerTooltip: 'Average Call Length'
  },
  callLengthInSeconds: {
    headerName: 'TCL',
    headerTooltip: 'Total Call Length',
    hide: false,
  },
  conversionAmount: {
    hide: false,
    headerTooltip: 'The gross amount of money earned from calls generated',
    comparator: compareCurrencyValues
  },
  payoutCount: {
    headerTooltip: 'Calls that meet the campaign\'s configured payout requirements',
  },
  profitGross: {
    hide: false,
    headerTooltip: 'The net amount of money earned from calls after expenses',
    comparator: compareCurrencyValues
  },
  payoutAmount: {
    hide: false,
    headerTooltip: 'The amount of money to be paid to your publisher for calls that meet the campaign’s criteria',
    comparator: compareCurrencyValues
  },
  convertedPercent: {
    hide: false,
    headerTooltip: 'The percentage of calls that resulted in a conversion'
  },
  earningsPerCallGross: {
    hide: false,
    flex: 68,
    minWidth: 60,
    headerTooltip: 'Revenue Per Call',
    headerName: 'RPC',
    comparator: compareCurrencyValues
  },
  profitMarginGross: {
    hide: false,
    headerTooltip: 'The percentage of money that is considered profit'
  },
  totalCost: { valueFormatter: (params) => costCurrencyFormatter.format(params.value), headerTooltip: 'Total Call Cost', comparator: compareCurrencyValues },
  datesByYear: { headerName: 'Year', valueFormatter: (params) => moment.parseZone(params.value).format('YYYY'), sortable: false },
  datesByYearMonth: { headerName: 'Month', valueFormatter: (params) => moment.parseZone(params.value).format('MMMM'), sortable: false },
  datesByYearMonthWeek: { headerName: 'Week', valueFormatter: (params) => moment.parseZone(params.value).format('w'), sortable: false },
  datesByYearMonthDay: { headerName: 'Date', valueFormatter: (params) => moment.parseZone(params.value).format('MMM D'), sortable: false },
  datesByYearMonthDayHour: { headerName: 'Hour', valueFormatter: (params) => moment.parseZone(params.value).format('MMM D HH:mm'), sortable: false },
  datesByYearMonthDayHourMinute: { headerName: 'Minute', valueFormatter: (params) => moment.parseZone(params.value).format('MMM D HH:mm'), sortable: false },
};

export const customPropsForNonAdminRoles = {
  [CALLLOG_REPORT_TYPE]: {
    publisher: {
      payoutAmount: {
        headerName: 'Revenue',
        headerTooltip: 'The amount of money to paid for calls that meet the campaign’s criteria'
      },
      hasPayout: {
        headerName: 'Converted'
      }
    },
    buyer: {
      conversionAmount: {
        headerName: 'Payout'
      },
      buyerConversionAmount: {
        headerName: 'Payout'
      },
      callCount: {
        headerName: 'Dialed',
        headerTooltip: 'All calls that have entered the Ringba system'
      },
      buyerCallLengthInSeconds: {
        headerName: 'Duration'
      },
      buyerConversionAdjustmentStatus: {
        headerName: 'Payout Adjustment Status'
      },
      // For quick insights filters in call logs
      convertedCalls: {
        headerName: 'Paid Out',
        headerTooltip: 'Calls that have met the campaign’s payout requirements'
      },
    }
  },
  [INSIGHT_REPORT_TYPE]: {
    publisher: {
      payoutCount: {
        headerName: 'Converted',
        headerTooltip: 'Calls that meet the campaign\'s configured conversion requirements'
      },
      payoutAmount: {
        headerName: 'Revenue',
        headerTooltip: 'The amount of money to be paid for calls that meet the campaign’s criteria'
      }
    },
    buyer: {
      convertedCalls: {
        headerName: 'Paid',
        headerTooltip: 'Calls that have met the campaign’s conversion requirements'
      },
      buyerInsightsConversionAmount: {
        headerName: 'Payout'
      },
      convertedPercent: {
        headerName: 'Paid Out %',
        headerTooltip: 'The percentage of calls that resulted in a Payout'
      }
    }
  }

};


export default class ReportingService {
  #recordingTempUrlCache = new Map();

  /** @ngInject **/
  constructor(
    $http,
    $q,
    ngAuthSettings
  ) {
    this.$http = $http;
    this.$q = $q;
    this.ngAuthSettings = ngAuthSettings;
  }

  getInsightsColDefs(accountId) {
    const url = `${this.#getBaseInsightsUrl(accountId)}/columns`;
    return this.$http.get(url);
  }

  getCallLogsColDefs(accountId) {
    const url = `${this.#getBaseCallLogsUrl(accountId)}/columns`;
    return this.$http.get(url);
  }

  validateInsightsRequest({ rowGroupCols }) {
    return rowGroupCols?.length > 0;
  }

  loadInsightData(accountId, requestData) {
    const activeData = store.getters['reporting/activeInsightData'];
    const requestCacheKey = this.#getCacheKeyFromRequest(requestData, activeData.refreshGridData, accountId);

    return this.#getInsightsData(accountId, requestData, activeData.dataCache, requestCacheKey, EXPIRE_INSIGHTS_DATA_CACHE_AFTER_SECONDS).then(({
      allRecords,
      totalsRecord,
      partialResult,
      lastUpdated,
      fromCache,
      cacheKey
    }) => {

      store.dispatch('reporting/updateInsightLastUpdatedTime', lastUpdated);

      if (!fromCache) {
        store.dispatch('reporting/setInsightsDataCache', {
          data: allRecords,
          cacheKey: cacheKey,
          partialResult,
          totalsRecord,
          lastUpdated
        });
      }

      return {
        records: this.getRowsForGrouping(allRecords, requestData.groupByColumns, requestData.groupKeys),
        allRecords,
        totalsRecord,
        partialResult
      };
    });
  }

  loadQuickInsightsData(accountId, requestData) {
    return this.#getInsightsData(accountId, requestData).then(({
      allRecords,
      totalsRecord,
      partialResult,
    }) => {
      return {
        allRecords,
        totalsRecord,
        partialResult
      };
    });
  }

  loadHistogramData(accountId, requestData) {
    return this.#getInsightsData(accountId, requestData).then(({
      allRecords,
      partialResult,
      lastUpdated,
      cacheKey,
      fromCache
    }) => {
      if (!fromCache) {
        store.dispatch('reporting/setCallLogsHistogramDataCache', {
          data: allRecords,
          cacheKey,
          partialResult,
          lastUpdated
        });
      }

      return {
        allRecords,
        partialResult
      };
    });
  }

  #getInsightsData(accountId, requestData, storeCache, cacheKey, expiryInSeconds) {
    if (storeCache && cacheKey) {
      const {
        dataFromCache,
        totalsRecordFromCache,
        partialResultFromCache,
        lastUpdatedFromCache,
      } = this.#getDataFromCache(cacheKey, storeCache, expiryInSeconds);

      if (dataFromCache) {
        return this.$q.when({
          allRecords: dataFromCache,
          totalsRecord: totalsRecordFromCache,
          partialResult: partialResultFromCache,
          lastUpdated: lastUpdatedFromCache,
          fromCache: true,
          cacheKey
        });
      }
    }

    const url = this.#getBaseInsightsUrl(accountId);

    return this.$http.post(url, requestData).then((response) => {
      return this.#handleInsightsDataResponse(response, requestData, cacheKey);
    });
  }

  #handleInsightsDataResponse(response, requestData, cacheKey) {
    const records = response.data.report.records;
    let totalsRecord = [];

    if (requestData.generateRollups) {
      totalsRecord = this.#getTotalRows(records, requestData.groupByColumns);
    }

    return {
      allRecords: records,
      totalsRecord,
      partialResult: response.data.report.partialResult,
      lastUpdated: new Date(),
      fromCache: false,
      cacheKey
    };
  }

  getReportingDateRange(timeRange, timeZoneId) {
    // Time is sent in UTC
    // If user selects 3PM in PST(UTC-08:00): the time which would be sent is 15:00 - 08:00 = 07:00
    const reportStart = moment.tz(timeRange[0].format('YYYY-MM-DD HH:mm:ss'), timeZoneId).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    const reportEnd = moment.tz(timeRange[1].format('YYYY-MM-DD HH:mm:ss'), timeZoneId).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

    return { reportStart, reportEnd };
  }

  getCallLogs(accountId, settings) {
    const url = this.#getBaseCallLogsUrl(accountId);

    return this.$http.post(url, settings).then(this.#handleCallLogsResponse.bind(this));
  }

  #handleCallLogsResponse(response) {
    let callLogs = response.data.report.records;
    callLogs = callLogs.map((call) => {
      return {
        ...call,
        callStatus: this.#getCallStatus(call)
      };
    });

    return {
      callLogs,
      totalCount: response.data.report.totalCount
    };
  }

  getCallDetails(accountId, requestData) {
    const url = `${this.#getBaseCallLogsUrl(accountId)}/detail`;

    return this.$http.post(url, requestData).then((response) => {
      return response.data.report.records[0];
    });
  }

  // The raw storage key is reported on the call as an event value once the
  // recording worker migrates the file; it is the id the tempUrl endpoint expects.
  getRecordingKeyFromCallDetails(callDetails) {
    const events = callDetails?.events || [];
    const migratedEvent = events.find((event) => event.name?.toLowerCase() === 'callrecordingmigrated');
    const keyEntry = migratedEvent?.eventStrVals?.find((entry) => entry.name?.toLowerCase() === 'recordingkey');

    return keyEntry?.value || null;
  }

  getRecordingTempUrl(accountId, recordingKey) {
    const cacheKey = `${accountId}/${recordingKey}`;
    const cached = this.#recordingTempUrlCache.get(cacheKey);

    if (cached && Date.now() - cached.mintedAt < RECORDING_TEMP_URL_CACHE_MS) {
      return this.$q.resolve(cached.url);
    }

    const url = `${this.ngAuthSettings.apiServiceBaseUri}${accountId}/recording/${encodeURIComponent(recordingKey)}/tempUrl`;

    return this.$http.get(url, { params: { timeInSeconds: RECORDING_TEMP_URL_LIFETIME_SECONDS } })
      .then((response) => {
        // The API serializes the result dictionary without a fixed casing contract
        const tempUrl = response.data?.tempUrl?.url || response.data?.tempUrl?.Url;

        if (!tempUrl) {
          return this.$q.reject(new Error('Recording tempUrl response contained no url'));
        }

        this.#recordingTempUrlCache.set(cacheKey, { url: tempUrl, mintedAt: Date.now() });

        return tempUrl;
      });
  }

  getFilterFromFilterCriteria(filterCriteria) {
    const filters = [];

    if (filterCriteria) {
      filterCriteria.forEach(andCriteria => {
        const anyConditionToMatch = [];

        andCriteria.tagCriteria.forEach(orCriteria => {
          anyConditionToMatch.push({
            column: orCriteria.filterField,
            value: orCriteria.value,
            isNegativeMatch: orCriteria.comparisonTypeTemp.isNegativeMatch,
            comparisonType: orCriteria.comparisonTypeTemp.key
          });
        });

        filters.push({
          anyConditionToMatch
        });
      });
    }

    return filters;
  }

  getRowsForGrouping(records, groupCols, groupKeys) {
    let includeRowGroups = [];
    let excludeRowGroups = [];

    includeRowGroups = groupCols.map((col, index) => {
      return {
        column: col.column,
        value: groupKeys[index]
      };
    }).slice(0, groupKeys.length + 1);

    excludeRowGroups = groupCols.slice(groupKeys.length + 1).map((column) => {
      return { column: column.column };
    });

    return records.filter((record) => {
      let filter = true;

      for (const group of includeRowGroups) {
        filter = group.value ? record[group.column] === group.value : typeof record[group.column] !== 'undefined';
        if (!filter) {
          return false;
        }
      }

      for (const group of excludeRowGroups) {
        filter = typeof record[group.column] === 'undefined';

        if (!filter) {
          return false;
        }
      }

      return filter;
    });
  }


  getFieldValuesForTimeRange(accountId, fieldName, fieldValuePrefix, dateRange) {
    const url = `${this.#getBaseCallLogsUrl(accountId)}/v2/search/${fieldName}/values?val=${encodeURIComponent(fieldValuePrefix)}`;

    return this.$http.post(url, dateRange);
  }

  checkIfFieldExistsInCriteria(criteria = [], tagId) {
    return criteria.some(andCriteria => {
      return andCriteria.tagCriteria.some(orCriteria => {
        return orCriteria.tagId === tagId;
      });
    });
  }

  #getTotalRows(records, groupByColumns) {
    const totalIndex = records.findIndex(record => {
      let isTotals = true;
      groupByColumns.some(column => {
        isTotals = typeof record[column.column] === 'undefined';

        return !isTotals;
      });

      return isTotals;
    });

    return records.splice(totalIndex, 1);
  }

  #getDataFromCache(cacheKey, cacheDataFromStore, expiryInSeconds) {
    const result = {
      requestCacheKey: cacheKey,
      expired: false
    };

    if (cacheDataFromStore.cacheKey === cacheKey) {
      if (this.#checkIfDataExpired(cacheDataFromStore.lastUpdated, expiryInSeconds)) {
        return {};
      }

      result.dataFromCache = cacheDataFromStore.data;
      result.partialResultFromCache = cacheDataFromStore.partialResult;
      result.totalsRecordFromCache = cacheDataFromStore.totalsRecord;
      result.lastUpdatedFromCache = cacheDataFromStore.lastUpdated;
    }

    return result;
  }


  #checkIfDataExpired(lastUpdated, expireInSeconds = 60) {
    const currentTimestamp = new Date().valueOf();

    return (lastUpdated.valueOf() + (expireInSeconds * 1000))
      < currentTimestamp;
  }

  #getCacheKeyFromRequest(requestData, refreshGridData, accountId) {
    return accountId + refreshGridData + requestData.reportStart + requestData.reportEnd
      + requestData.viewAsPubId + requestData.viewAsBuyerId
      + requestData.groupByColumns.reduce((k, c) => (k += c.column, k), '')
      + requestData.valueColumns.reduce((k, c) => (k += c.column + c.aggregateFunction, k), '')
      + requestData.orderByColumns.reduce((k, c) => (k += c.column + c.direction, k), '')
      + requestData.filters.reduce((hash, andFilter, index) => {
        hash += index + ';;' +
          andFilter.anyConditionToMatch.reduce((orHash, orFilter) => {
            orHash += orFilter.column + ';' +
              orFilter.value + ';' +
              (orFilter.isNegativeMatch || '') + ';' +
              (orFilter.comparisonType || ';');
            return orHash;
          }, '');
        return hash;
      }, '');
  }

  #getBaseInsightsUrl(accountId) {
    return `${this.ngAuthSettings.apiServiceBaseUri}${accountId}/insights`;
  }

  #getBaseCallLogsUrl(accountId) {
    return `${this.ngAuthSettings.apiServiceBaseUri}${accountId}/calllogs`;
  }

  #getCallStatus(callLog) {
    let status = 'notConnected';
    const role = store.getters['reporting/role'];

    if (callLog.hasConnected || callLog.buyerHasConnected) {
      status = 'completed';
    }

    if (role !== 'publisher') {
      if (callLog.hasConverted) {
        status = 'converted';
      } else if (callLog.buyerHasConverted) {
        status = 'buyer-converted';
      }
    }

    if (role === 'publisher' && callLog.hasConnected) {
      status = 'publisher-completed';
    }

    if (callLog.wasBlocked) {
      status = 'blocked';
    }

    if (callLog.isLive) {
      status = 'live';
    }

    return statusToTooltipMapping[status];
  }
}

export function convertApi2AgGridColDef(apiColDef, minWidth = 100, flex = 3, role) {
  return Object.assign({}, {
    headerName: apiColDef.title || apiColDef.id,
    headerTooltip: apiColDef.title || apiColDef.id,
    field: apiColDef.id,
    colId: apiColDef.id,
    type: apiColDef.type,
    hide: true,
    enableRowGroup: apiColDef.supportsGrouping,
    enablePivot: apiColDef.supportsPivot,
    enableValue: apiColDef.isAggregate,
    suppressColumnsToolPanel: apiColDef.supportsGrouping,
    aggFunc: apiColDef.isAggregate ? apiColDef.aggregateTypes[0].toLowerCase() : null,
    allowedAggFuncs: apiColDef.aggregateTypes && apiColDef.aggregateTypes.map(t => t.toLowerCase()),
    sortable: apiColDef.supportsSorting !== undefined ? apiColDef.supportsSorting : true,
    ringbaGroupName: apiColDef.groupName,
    filterGroupName: apiColDef.filterGroupName,
    enableFilter: apiColDef.supportsFilter,
    suppressFiltersToolPanel: apiColDef.supportsFilter,
    flex,
    minWidth,
  }, customColumnProps[apiColDef.id], customPropsForNonAdminRoles[INSIGHT_REPORT_TYPE][role]?.[apiColDef.id]);
}

export function convertApi2AgGridColDefForCallLogs(apiColDef, role) {
  const cellRenderer = function defaultCellRendererCallLogs(params) {
    let value = '';

    if ((typeof params.valueFormatted === 'undefined' || params.valueFormatted === null) && typeof params.value !== 'undefined') {
      value = params.value;
    } else if (typeof params.value !== 'undefined') {
      value = params.valueFormatted;
    }

    if (params.colDef?.enableFilter) {
      if (!value || value === '') {
        return '';
      }

      const linkColor = colToAtributeIdMapping[params.colDef?.colId] ? 'contrast' : '';

      return Object.assign(document.createElement('span'), {
        title: 'Click to Filter',
        className: `filterable-cell ${linkColor}`,
        textContent: value,
      });
    }

    return Object.assign(document.createElement('span'), { textContent: value });
  };
  return Object.assign({cellRenderer}, convertApi2AgGridColDef(apiColDef, 55), specificColsProperties[apiColDef.id], customPropsForNonAdminRoles[CALLLOG_REPORT_TYPE][role]?.[apiColDef.id]);
}

export function convertTag2AgGridColDef(tag, colDefOverrides = {}) {
  const tagId = tag.tagType + ':' + tag.tagName;
  return Object.assign({},
    {
      headerName: tagId,
      field: 'tag:' + tagId,
      colId: 'tag:' + tagId,
      type: 'tag',
      hide: true,
      enableRowGroup: true,
      enablePivot: false,
      enableValue: false,
      ringbaGroupName: 'Tags',
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
    },
    colDefOverrides);
}

export function getStateMappingAndRoute(colId, colData) {
  // We get the corresponding Attribute for the column Id
  const attributeProperty = colToAtributeIdMapping[colId];
  // With the attribute we get the ID
  const attributeId = colData[attributeProperty];

  let attributeInitials = '';
  if (attributeId) {
    // WE get the initials to know what kind of object it is (buyer, campaign, target, ptt)
    attributeInitials = attributeId.slice(0, 2);
  }

  let routeParams;

  const stateMappingCopy = stateMapping[attributeInitials];
  if (stateMappingCopy) {
    routeParams = {
      [stateMappingCopy.routeIdAttribute]: attributeId
    };
  }
  return {stateMappingCopy, routeParams};
}

export function convertApiColDefs2Filters(apiColDefs, role, reportType) {
  apiColDefs = apiColDefs || [];

  return apiColDefs.reduce((normalized, colDef) => {
    if (colDef.supportsFilter) {
      normalized.push({
        tagName: colDef.id,
        tagType: colDef.filterGroupName || 'Others',
        tagSource: 'Filters',
        isRange: colDef.isRange,
        fieldType: colDef.type,
        fieldName: colDef.id,
        displayName: customPropsForNonAdminRoles[reportType][role]?.[colDef.id]?.headerName || colDef.title,
        isTag: false,
        roles: colDef.roles,
        camelCaseFormatTagType: false
      });
    }

    return normalized;
  }, []);
}

export function convertColsToGroupBys(apiColDefs) {
  apiColDefs = apiColDefs || [];

  // {
  //   name: 'Campaign Insights',
  //   field: 'campaignName',
  //   helperText: 'Data segmented by campaign'
  // },

  return apiColDefs.reduce((normalized, colDef) => {
    if (colDef.supportsGrouping) {
      normalized[colDef.groupName] = normalized[colDef.groupName] || [];
      normalized[colDef.groupName].push({
        field: colDef.id,
        helperText: '',
        displayName: colDef.title,
        groupName: colDef.groupName
      });
    }

    return normalized;
  }, {});
}

export function convertTagsToGroupBys(tags) {
  tags = tags || [];

  // {
  //   tags: true,
  //   values: {
  //     'address': ['zip code', 'city', 'state', 'country', 'pincde', 'street 1', 'street 2', 'landmark', 'nearby']
  //   }
  // }
  return tags.reduce((normalized, tag) => {
    normalized[tag.tagType] = normalized[tag.tagType] || [];
    normalized[tag.tagType].push({
      name: tag.tagName,
      tagType: tag.tagType
    });
    return normalized;
  }, {});
}

export function checkTimeInterval(diff) {
  if (diff.asHours() <= 2) {
    return 'minute';
  }

  const diffInDays = Math.ceil(diff.asDays());

  if (diffInDays <= 1) {
    return 'hour';
  } else if (diffInDays > 1 && diffInDays < 7) {
    return 'day';
  } else if (diffInDays >= 28 && diffInDays <= 31) {
    return 'week';
  } else if (diffInDays > 31 && diffInDays <= 366) {
    return 'month';
  } else if (diffInDays > 366) {
    return 'year';
  }

  return 'day';
}

function _getCachedFunctionForMainGroup(mainGroupChildren) {
  const subGroupChildrenCache = {};

  return function add(col) {
    const group = col.ringbaGroupName || '';

    if (group) {
      let subGroup = subGroupChildrenCache[group];

      if (!subGroup) {
        subGroup = {
          headerName: group,
          children: []
        };

        mainGroupChildren.push(subGroup);
      }

      subGroup.children.push(col);

      subGroupChildrenCache[group] = subGroup;
    } else {
      mainGroupChildren.push(col);
    }
  };
}

export function groupInsightsColDefs(colDefs) {
  if (!colDefs.length) {
    return [];
  }

  const groupChildren = [];
  const valueChildren = [];

  const addToAggregateChildren = _getCachedFunctionForMainGroup(valueChildren);

  colDefs.forEach((col) => {
    if (col.enableRowGroup) {
      groupChildren.push(col);
    }

    if (col.enableValue) {
      addToAggregateChildren(col);
    }
    // Fix disappearing row group
    if (col.ringbaGroupName === 'Popular') {
      const def = { ...col };
      def.ringbaGroupName = null;
      addToAggregateChildren(def);
    }
  });

  return [{
    headerName: 'Breakdown Levels',
    toolPanelClass: 'ag-grid-tool-panel-column-indicator ag-grid-tool-panel-column-indicator--group',
    children: groupChildren
  },
  {
    headerName: HEADER_NAME_COLUMNS,
    toolPanelClass: 'ag-grid-tool-panel-column-indicator ag-grid-tool-panel-column-indicator--value',
    children: valueChildren
  }];
}

export function groupCallLogsColDefs(colDefs) {
  if (!colDefs.length) {
    return [];
  }

  const valueChildren = [];

  const addToAggregateChildren = _getCachedFunctionForMainGroup(valueChildren);

  colDefs.forEach((col) => {
    addToAggregateChildren(col);
  });

  return valueChildren;
}

export function returnGroupByCols(apiColDefs) {
  apiColDefs = apiColDefs || [];

  return apiColDefs.reduce((groupings, col) => {
    if (col.supportsGrouping) {
      groupings.push({
        id: col.id,
        displayName: col.title
      });
    }

    return groupings;
  }, []);
}

export function getDefaultChartParameters() {
  return [
    {
      name: 'totalCost',
      title: 'Total Cost',
      dataType: 'currency',
      totalValue: null,
      color: '#4a74bd',
      aggFunc: 'Sum'
    },
    {
      name: 'profitGross',
      title: 'Profit',
      dataType: 'currency',
      totalValue: null,
      color: '#669bb3',
      aggFunc: 'Sum'
    },
    {
      name: 'payoutAmount',
      title: 'Payout',
      dataType: 'currency',
      totalValue: null,
      color: '#f15c80',
      aggFunc: 'Sum'
    },
    {
      name: 'conversionAmount',
      title: 'Conversion',
      dataType: 'currency',
      totalValue: null,
      color: '#90ed7d',
      aggFunc: 'Sum'
    },
    {
      name: 'callCount',
      title: 'Inbound Calls',
      dataType: 'count',
      totalValue: null,
      color: '#e4d354',
      aggFunc: 'Count'
    },
    {
      name: 'connectedCallCount',
      title: 'Connected Calls',
      // stackWithParameters: [
      //   ...getParametersOptions(['error', 'noConnect', 'connected', 'noTarget'])
      // ],
      dataType: 'count',
      totalValue: null,
      color: '#b5dcf5',
      aggFunc: 'Count'
    },
    {
      name: 'convertedCalls',
      title: 'Converted Calls',
      dataType: 'count',
      totalValue: null,
      color: '#24CBE5',
      aggFunc: 'Count'
    },
  ];
}
